import { Col, Container, Row } from "react-grid-system";
import { useQuery } from "react-query";
import { Sidemenu } from "../../components/Sidemenu";
import { Users } from "../../components/Users";
import { OrganizationAdminsList } from "../../components/Organizations";
import { QueryLoader } from "../../components/Loader";
import { StyledPageHeader } from "../../styles/StyledPage";
import { STable } from "../../styles/AdminSearchScore";
import { STab, STabList, STabPanel, STabs } from "../../styles/Tabs";
import { getUnitsWithMembers } from "../../api/unit/unit";
import { Role } from "../../constants/role";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useCurrentUser from "../../hooks/useCurrentUser";

/** One row per unit admin, with every unit they are admin of. */
function unitAdmins(units) {
	const byUser = new Map();
	for (const unit of units) {
		for (const member of unit.members ?? []) {
			if (!member.roles?.includes(Role.admin)) continue;
			const row = byUser.get(member.user.id) ?? { user: member.user, units: [] };
			row.units.push(unit.name);
			byUser.set(member.user.id, row);
		}
	}
	return [...byUser.values()].sort((a, b) =>
		`${a.user.firstName} ${a.user.lastName}`.localeCompare(`${b.user.firstName} ${b.user.lastName}`)
	);
}

function UnitAdminsList() {
	const { axiosPrivate } = useAxiosPrivate();
	const { data, isLoading, isError } = useQuery("units-with-members", () => getUnitsWithMembers(axiosPrivate));

	if (isLoading) return <QueryLoader />;
	if (isError) return <p>Unit admins could not be loaded.</p>;

	const rows = unitAdmins(data);
	return (
		<STable>
			<thead>
				<tr>
					<td colSpan={2}>Name</td>
					<td colSpan={2}>Email</td>
					<td colSpan={2}>Unit</td>
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 ? (
					<tr>
						<td colSpan={6}>No unit admins found</td>
					</tr>
				) : (
					rows.map(({ user, units }) => (
						<tr key={user.id}>
							<td colSpan={2}>
								{user.firstName} {user.lastName}
							</td>
							<td colSpan={2} style={{ overflowWrap: "anywhere" }}>
								{user.email}
							</td>
							<td colSpan={2}>{units.sort().join(", ")}</td>
						</tr>
					))
				)}
			</tbody>
		</STable>
	);
}

/** Root sees the admins of every organization. */
function RootTabs() {
	return (
		<STabs selectedTabClassName="is-selected" selectedTabPanelClassName="is-selected">
			<STabList>
				<STab>Organization admins</STab>
			</STabList>
			<STabPanel>
				<OrganizationAdminsList />
			</STabPanel>
		</STabs>
	);
}

function OrganizationTabs() {
	const { can } = useCurrentUser();
	const managesUsers = can("manageUsers");

	return (
		<STabs selectedTabClassName="is-selected" selectedTabPanelClassName="is-selected">
			<STabList>
				<STab>Unit admins</STab>
				{managesUsers && <STab>Users</STab>}
			</STabList>
			<STabPanel>
				<UnitAdminsList />
			</STabPanel>
			{managesUsers && (
				<STabPanel>
					<Users />
				</STabPanel>
			)}
		</STabs>
	);
}

export default function PeoplePage() {
	const { can } = useCurrentUser();

	return (
		<Container>
			<Row style={{ marginTop: "2rem" }}>
				<Col sm={12} lg={2}>
					<Sidemenu />
				</Col>
				<Col sm={12} lg={10}>
					<StyledPageHeader>
						<h1>People</h1>
					</StyledPageHeader>
					{can("manageOrganizations") ? <RootTabs /> : <OrganizationTabs />}
				</Col>
			</Row>
		</Container>
	);
}
